import { useState, useEffect, useCallback } from 'react';
import { getUsers, createUser, deleteUser, getAssignableRoles, getErrorMessage } from '../../api/auth';
import { useAuth } from '../../context/AuthContext';

// Gestion des comptes.
// - Super Administrateur : peut créer Administrateur + les 3 niveaux, tout supprimer.
// - Administrateur : peut créer/supprimer uniquement les 3 niveaux (Débutant/Intermédiaire/Avancé).
export default function AccountsPanel() {
  const { token, user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setListError('');
    Promise.all([getUsers(token), getAssignableRoles(token)])
      .then(([userList, roleList]) => {
        setUsers(userList);
        setRoles(roleList);
        if (roleList.length > 0) setRole((prev) => prev || roleList[0].value);
      })
      .catch((err) => setListError(getErrorMessage(err, 'Impossible de charger les comptes.')))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (username.trim().length < 3) {
      setFormError("Le nom d'utilisateur doit contenir au moins 3 caractères.");
      return;
    }
    if (password.length < 6) {
      setFormError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (!role) {
      setFormError('Veuillez choisir un rôle.');
      return;
    }

    setSubmitting(true);
    try {
      await createUser(token, username.trim(), password, role, email.trim());
      setFormSuccess(`Compte "${username.trim()}" créé avec succès.`);
      setUsername('');
      setEmail('');
      setPassword('');
      load();
    } catch (err) {
      setFormError(getErrorMessage(err, 'Impossible de créer ce compte.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userToDelete) => {
    if (!window.confirm(`Supprimer le compte "${userToDelete.username}" ?`)) return;
    try {
      await deleteUser(token, userToDelete.id);
      load();
    } catch (err) {
      setListError(getErrorMessage(err, 'Impossible de supprimer ce compte.'));
    }
  };

  const roleBadgeClass = (r) => {
    if (r === 'super_admin') return 'role-badge role-super-admin';
    if (r === 'admin') return 'role-badge role-admin';
    return 'role-badge role-niveau';
  };

  return (
    <div className="admin-grid">
      <div className="admin-card">
        <h2>Créer un compte</h2>
        <form onSubmit={handleCreate} className="auth-field-group">
          {formError && <div className="auth-error">{formError}</div>}
          {formSuccess && <div className="auth-success">{formSuccess}</div>}

          <div className="auth-field">
            <label htmlFor="new-username">Nom d'utilisateur</label>
            <input
              id="new-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="ex : operateur1"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="new-email">Email (optionnel)</label>
            <input
              id="new-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ex : operateur1@meapulse.ma"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="new-password">Mot de passe</label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="6 caractères minimum"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="new-role">Rôle</label>
            <select id="new-role" value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Création…' : 'Créer le compte'}
          </button>
        </form>
      </div>

      <div className="admin-card">
        <h2>Comptes existants</h2>
        {listError && <div className="auth-error">{listError}</div>}

        {loading ? (
          <p className="admin-empty">Chargement…</p>
        ) : users.length === 0 ? (
          <p className="admin-empty">Aucun compte trouvé.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Nom d'utilisateur</th>
                <th>Email</th>
                <th>Rôle</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>{u.email || '—'}</td>
                  <td><span className={roleBadgeClass(u.role)}>{u.role_label}</span></td>
                  <td>
                    {u.id !== currentUser?.id && (
                      <button className="btn-danger-link" onClick={() => handleDelete(u)}>
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
